import { Injectable, computed, effect, inject, signal } from '@angular/core';
import { Subscription } from 'rxjs';
import { ImportedReport, ReportRepositoryService } from '@xmedical/core';
import { PatientStore } from './patient.store';
/**
 * PatientImportedReportsStore — read-only legacy reports for migrated
 * practices.
 *
 * Only ~10% of practices ever populate this list (those imported from
 * the legacy platform). No mutations, no push subscription. Lives as
 * its own store so it can be cleanly removed when the last legacy
 * practice migrates off.
 */
@Injectable({
  providedIn: 'root'
})
export class PatientImportedReportsStore {
  private patientStore = inject(PatientStore);
  private repo = inject(ReportRepositoryService);

  private readonly _items = signal<ImportedReport[]>([]);
  private readonly _loading = signal<boolean>(false);
  private readonly _error = signal<string | null>(null);

  readonly items = this._items.asReadonly();
  readonly loading = this._loading.asReadonly();
  readonly error = this._error.asReadonly();
  readonly count = computed(() => this._items().length);
  // Cancels any in-flight `fetch()` HTTP when a newer request arrives.
  private fetchSub?: Subscription;

  constructor() {
    effect(() => {
      const patient = this.patientStore.current();
      if (!patient) {
        this.fetchSub?.unsubscribe();
        this._loading.set(false);
        this._items.set([]);
        this._error.set(null);
        return;
      }
      this.fetch(patient.id);
    });
  }

  reload(): void {
    const patient = this.patientStore.current();
    if (!patient) return;
    this.fetch(patient.id);
  }

  private fetch(patientId: number): void {
    this.fetchSub?.unsubscribe();
    this._loading.set(true);
    this._error.set(null);
    this.fetchSub = this.repo.loadPatientImportedReports(patientId).subscribe({
      next: reports => {
        this._items.set(reports);
        this._loading.set(false);
      },
      error: error => {
        this._loading.set(false);
        this._error.set('Could not load patient imported reports');
        
      }
    });
  }
}
